import { useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Pin } from 'lucide-react'
import clsx from 'clsx'

import { crmErrorDetail, crmListService, type CrmContactList } from '@/services/crm'

/**
 * Put one contact on a list, or take it off, from wherever the contact is.
 *
 * A list is a rule, so most membership is decided by the rule; a checkbox
 * here is the override — a pin puts someone on a list the rule would not
 * select, an exclusion keeps them off one it would. The server stores both
 * (`pinnedIds` / `excludedIds`) and has since the lists shipped; nothing in
 * the UI ever set them, so "add this person to my list" was unbuildable by
 * hand until this.
 *
 * Membership is read from the server's own evaluation, one query per list,
 * because the rule engine lives there and a copy here would drift. The
 * queries share keys with the page's list filter, so a list opened in the
 * table costs nothing extra to show here.
 */
export default function AddToListPopover({
  contactId,
  contactName,
}: {
  contactId: string
  contactName: string
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const lists = useQuery({
    queryKey: ['crm', 'lists'],
    queryFn: crmListService.list,
    staleTime: 30_000,
  })
  const rows = lists.data?.lists ?? []

  const membership = useQueries({
    queries: rows.map((list) => ({
      queryKey: ['crm', 'lists', list.id, 'members'],
      queryFn: () => crmListService.members(list.id),
      staleTime: 30_000,
    })),
  })

  const setMembers = useMutation({
    mutationFn: ({ list, on }: { list: CrmContactList; on: boolean }) =>
      crmListService.setMembers(list.id, on ? { add: [contactId] } : { remove: [contactId] }),
    onSuccess: async () => {
      setError(null)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] }),
      ])
    },
    onError: (failure) => setError(crmErrorDetail(failure, 'The list could not be changed.')),
  })

  return (
    <div className="w-[240px] px-1 py-0.5" aria-label={`Lists for ${contactName}`}>
      <div className="px-1 pb-1 font-mono text-[8px] uppercase tracking-[0.18em] text-stone-600">
        Lists · {contactName}
      </div>
      {lists.isLoading ? <div className="px-1 py-1 text-[10px] text-stone-500">Reading lists…</div> : null}
      {lists.isError ? (
        <div className="px-1 py-1 text-[10px] text-red-300">The lists could not be read.</div>
      ) : null}
      {lists.isSuccess && rows.length === 0 ? (
        <div className="px-1 py-1 text-[10px] text-stone-500">No lists yet.</div>
      ) : null}
      <ul className="max-h-[260px] space-y-0.5 overflow-y-auto scrollbar-thin">
        {rows.map((list, index) => {
          const members = membership[index]
          const pinned = list.pinnedIds.includes(contactId)
          const excluded = list.excludedIds.includes(contactId)
          const byRule = members.data ? members.data.contacts.some((c) => c.id === contactId) : null
          // On the list when the server says so; a pin or an exclusion is
          // already folded into that answer, so this is the truth of it.
          const on = byRule ?? pinned
          const busy = setMembers.isPending && setMembers.variables?.list.id === list.id
          return (
            <li key={list.id}>
              <label
                className={clsx(
                  'flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-amber-400/[0.06]',
                  busy && 'opacity-60',
                )}
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={busy || members.isLoading}
                  onChange={(event) => setMembers.mutate({ list, on: event.target.checked })}
                  aria-label={`${on ? 'Remove from' : 'Add to'} ${list.name}`}
                  className="h-3 w-3 accent-amber-400"
                />
                <span className="min-w-0 flex-1 truncate text-stone-300">{list.name}</span>
                {list.isSystem ? <Lock size={9} className="text-stone-600" aria-label="Built in" /> : null}
                {pinned ? (
                  <Pin size={9} className="text-amber-300" aria-label="Pinned to this list" />
                ) : excluded ? (
                  <span className="text-[8px] uppercase tracking-wider text-stone-600">excluded</span>
                ) : byRule ? (
                  <span className="text-[8px] uppercase tracking-wider text-stone-600">by rule</span>
                ) : members.isLoading ? (
                  <span className="text-[8px] text-stone-700">…</span>
                ) : null}
              </label>
            </li>
          )
        })}
      </ul>
      {error ? <div className="px-1 pt-1 text-[10px] text-red-300">{error}</div> : null}
    </div>
  )
}
